import { type RefObject, useEffect, useRef } from "react";
import { usePubSub, transitionDurations } from "@huub-web/ui";

const DEBOUNCE_MS = 150;
const ANIMATION_BUFFER_MS = 50;

type UseAutoScrollToPanelParams = {
  /**
   * Ref to the container element that wraps all panels.
   * Used to scope DOM queries for highlighted field lookup.
   */
  containerRef: RefObject<HTMLElement | null>;
  /**
   * The currently active panel index from FormFlowTemplate state.
   * Tracked via a ref so the delayed panel-navigation scroll reads the
   * latest value after the animation completes.
   */
  currentPanelIndex: number;
  /**
   * The form's control field prefix (e.g. `"form-3909__"`).
   * Used to scope panel navigation events to this form instance only.
   */
  controlPrefix: string;
};

/**
 * Auto-scrolls when the AI agent interacts with form panels:
 *
 * 1. **Field highlights** — scrolls the highlighted field element itself
 *    into view. This handles tall panels with sub-sections where the panel
 *    header may be visible but the target field is still off-screen.
 * 2. **Panel navigation** (`__nextPanel`, `__previousPanel`, `__expand`) —
 *    scrolls the panel header into view after the open/close animation.
 *
 * Only reacts to agent-driven PubSub events — manual user clicks
 * (Next button, Edit button) do not trigger scroll.
 */
export const useAutoScrollToPanel = ({
  containerRef,
  currentPanelIndex,
  controlPrefix,
}: UseAutoScrollToPanelParams) => {
  const pubSub = usePubSub();
  // Ref avoids re-subscribing when the context object identity changes.
  // Safe because PubSubProvider's value is stable for the component lifetime.
  const pubSubRef = useRef(pubSub);
  pubSubRef.current = pubSub;
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const panelScrollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const currentPanelIndexRef = useRef(currentPanelIndex);
  currentPanelIndexRef.current = currentPanelIndex;

  useEffect(() => {
    const scrollPanelHeader = (panelIndex: number) => {
      const panel = containerRef.current?.querySelector(
        `[data-panel-index="${panelIndex}"]`,
      );
      if (!panel) return;

      const header = panel.querySelector("[data-panel-header]");
      if (!header) return;

      header.scrollIntoView({ behavior: "smooth", block: "center" });
    };

    const unsubscribe = pubSubRef.current.subscribe(
      "inputUpdate",
      (payload) => {
        if (!payload || !containerRef.current) return;

        const updates = Array.isArray(payload) ? payload : [payload];

        // Primary trigger: highlighted field updates → scroll to the field itself.
        // Takes priority over panel navigation if both are in the same batch,
        // since scrolling to the field is more specific than scrolling to a header.
        const firstHighlighted = updates.find((u) => u?.highlight === true);
        if (firstHighlighted?.id) {
          if (debounceTimerRef.current !== null) {
            clearTimeout(debounceTimerRef.current);
          }
          debounceTimerRef.current = setTimeout(() => {
            const escapedId = CSS.escape(firstHighlighted.id);
            const fieldElement = containerRef.current?.querySelector(
              `[data-field-key="${escapedId}"]`,
            );
            if (!fieldElement) return;

            fieldElement.scrollIntoView({
              behavior: "smooth",
              block: "center",
            });
          }, DEBOUNCE_MS);
          return;
        }

        // Secondary trigger: agent panel navigation controls scoped to this form
        const isPanelNavigation = updates.some(
          (u) =>
            u?.id?.startsWith(controlPrefix) &&
            (u.id.endsWith("__nextPanel") ||
              u.id.endsWith("__previousPanel") ||
              u.id.endsWith("__expand")),
        );
        if (isPanelNavigation) {
          if (panelScrollTimerRef.current !== null) {
            clearTimeout(panelScrollTimerRef.current);
          }
          panelScrollTimerRef.current = setTimeout(
            () => {
              scrollPanelHeader(currentPanelIndexRef.current);
            },
            transitionDurations.m * 1000 + ANIMATION_BUFFER_MS,
          );
        }
      },
    );

    return () => {
      unsubscribe();
      if (debounceTimerRef.current !== null) {
        clearTimeout(debounceTimerRef.current);
      }
      if (panelScrollTimerRef.current !== null) {
        clearTimeout(panelScrollTimerRef.current);
      }
    };
  }, [containerRef, controlPrefix]);
};
